<script lang="ts" setup>
  import { ref, onMounted, type Ref, onBeforeUnmount, onUpdated } from 'vue';
  import type { SwiperOptions } from 'swiper/types';
  import type { NavButtons } from '@storefront/types';
  import Swiper from 'swiper';
  import { Navigation, Autoplay } from 'swiper/modules';
  import 'swiper/css';
  import 'swiper/css/navigation';
  import { viewItemListEvent, selectPromotionEvent, useGoogleTagManager } from '@storefront/storefront';
  import { useShared } from '@othercommerce/foundation';
  import type { ProductListingResource } from '@storefront/resources';

  interface Props {
    products: ProductListingResource[];
    parameters?: SwiperOptions;
    productHeader?: string;
    gtagSaleBlockData?: {
      type: string
      header: string;
      placement: string;
    };
    enableAutoplay?: boolean,
    timeAutoplay?: number | string,
    speed?: number | string,
  }

  const props = withDefaults(defineProps<Props>(), {
    productHeader: 'h3',
    enableAutoplay: false,
    timeAutoplay: 3000,
    speed: 400,
  });

  const htmlElement = ref() as Ref<HTMLElement>;
  const htmlNavigation = ref() as Ref<NavButtons>;
  const swiper = ref() as Ref<Swiper>;
  const shared = useShared();
  const gtm = useGoogleTagManager();

  function initializeSwipers() {
    swiper.value = new Swiper(htmlElement.value, {
      modules: [Navigation, Autoplay],
      spaceBetween: 16,
      ...props.parameters,
      autoplay: props.enableAutoplay ? { delay: Number(props.timeAutoplay ?? 3000) } : false,
      speed: Number(props.speed ?? 400),
      navigation: {
        nextEl: htmlNavigation.value.next.htmlElement,
        prevEl: htmlNavigation.value.prev.htmlElement,
      },
    });
  }

  function destroySwipers() {
    swiper.value?.destroy();
  }

  function onProductClick(product: ProductListingResource) {
    if (props.gtagSaleBlockData) {
      gtm.push(selectPromotionEvent(props.gtagSaleBlockData, product));
    }
  }

  onMounted(() => {
    gtm.push(viewItemListEvent(props.products as ProductListingResource[], shared.value.activeCurrencyCode));
    initializeSwipers();
  });

  onUpdated(() => {
    initializeSwipers();
  });

  onBeforeUnmount(() => {
    destroySwipers();
  });
</script>
<template>
  <div class="items-carousel products-carousel">
    <div ref="htmlElement" class="swiper items-carousel__container products-carousel__container">
      <div class="swiper-wrapper">
        <div v-if="$slots.before" class="swiper-slide items-carousel__slide products-carousel__product">
          <slot name="before" />
        </div>
        <div v-for="product in products" class="swiper-slide items-carousel__slide products-carousel__product">
          <ProductCard :header="productHeader" :product @click="onProductClick(product)" />
        </div>
      </div>
    </div>
    <SfCarouselButtons ref="htmlNavigation" />
  </div>
</template>
<style lang="scss">
  :root {
    --sf-products-carousel-padding: 0;
    --sf-products-carousel-container-padding: 0;
  }

  .products-carousel {
    padding: var(--sf-products-carousel-padding);

    &__container {
      padding: var(--sf-products-carousel-container-padding);

      .swiper-wrapper .products-carousel__product {
        border-radius: 0;
      }
    }
  }
</style>
